import { useParams, useNavigate } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, Package, AlertTriangle, CheckCircle2, XCircle, ShieldAlert } from 'lucide-react';
import { useInventoryStock } from '@/hooks/useInventory';
import { TransactionHistory } from '@/components/inventory/TransactionHistory';
import { format } from 'date-fns';
import type { InventoryStock } from '@/types/database/inventory';

type StockHealth = 'out-of-stock' | 'critical' | 'warning' | 'healthy';

function getStockHealth(item: InventoryStock): StockHealth {
  if (item.current_quantity <= 0) return 'out-of-stock';
  if (item.reorder_level > 0 && item.current_quantity <= item.reorder_level) return 'critical';
  if (item.reorder_level > 0 && item.current_quantity <= item.reorder_level * 1.5) return 'warning';
  return 'healthy';
}

const HEALTH_CONFIG: Record<StockHealth, { label: string; variant: 'destructive' | 'secondary' | 'outline' | 'default'; className: string; icon: typeof AlertTriangle; progressColor: string }> = {
  'out-of-stock': { label: 'Out of Stock', variant: 'destructive', className: '', icon: XCircle, progressColor: 'bg-destructive' },
  'critical': { label: 'Critical', variant: 'destructive', className: 'bg-harvest-warning text-white border-harvest-warning', icon: AlertTriangle, progressColor: 'bg-harvest-warning' },
  'warning': { label: 'Warning', variant: 'outline', className: 'border-harvest-warning text-harvest-warning', icon: ShieldAlert, progressColor: 'bg-harvest-warning' },
  'healthy': { label: 'Healthy', variant: 'outline', className: 'border-primary text-primary', icon: CheckCircle2, progressColor: 'bg-primary' },
};

export default function InventoryProductDetail() {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const { data: stock, isLoading } = useInventoryStock({ productId });

  const product = stock?.[0];

  if (isLoading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </Layout>
    );
  }

  if (!product) {
    return (
      <Layout>
        <div className="space-y-4">
          <Button variant="ghost" onClick={() => navigate('/inventory')}>
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Inventory
          </Button>
          <p className="text-muted-foreground">Product not found.</p>
        </div>
      </Layout>
    );
  }

  const health = getStockHealth(product);
  const config = HEALTH_CONFIG[health];
  const categoryName = (product.product as any)?.category?.name;
  const categoryType = (product.product as any)?.category?.category_type;
  const barValue = product.reorder_level > 0
    ? Math.min(Math.max((product.current_quantity / (product.reorder_level * 2)) * 100, 0), 100)
    : 100;

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/inventory')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold tracking-tight">
                {product.product?.name || 'Unknown Product'}
              </h1>
              <Badge variant={config.variant} className={`gap-1 ${config.className}`}>
                <config.icon className="h-3 w-3" />
                {config.label}
              </Badge>
            </div>
            <p className="text-muted-foreground">
              {[categoryName, categoryType].filter(Boolean).join(' · ') || 'Inventory details'}
            </p>
          </div>
        </div>

        {/* Stock Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Current Stock</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold font-mono">
                {product.current_quantity.toLocaleString()}
              </div>
              <p className="text-xs text-muted-foreground">{product.unit_of_measure}</p>
              <div className="mt-2 relative h-2 w-full overflow-hidden rounded-full bg-secondary">
                <div
                  className={`h-full rounded-full transition-all ${config.progressColor}`}
                  style={{ width: `${barValue}%` }}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Reorder Level</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold font-mono">
                {product.reorder_level.toLocaleString()}
              </div>
              <p className="text-xs text-muted-foreground">{product.unit_of_measure}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Status</CardDescription>
            </CardHeader>
            <CardContent>
              <Badge variant={config.variant} className={`gap-1 text-sm ${config.className}`}>
                <config.icon className="h-4 w-4" />
                {config.label}
              </Badge>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Last Updated</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-lg font-medium">
                {format(new Date(product.last_updated), 'MMM d, yyyy')}
              </div>
              <p className="text-xs text-muted-foreground">
                {format(new Date(product.last_updated), 'HH:mm')}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Latest Transactions */}
        <Card>
          <CardHeader>
            <CardTitle>Latest Transactions</CardTitle>
            <CardDescription>
              Recent inventory movements for this product
            </CardDescription>
          </CardHeader>
          <CardContent>
            <TransactionHistory 
              productId={productId} 
              limit={20} 
              currentStock={product.current_quantity}
              unitOfMeasure={product.unit_of_measure}
            />
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
